<template>
  <v-dialog
    v-model="dialog"
    max-width="720"
    persistent
    transition="scale-transition"
  >
    <v-card class="theme-card" rounded="pill">

      <v-card-title 
        class="theme-header"
        :style="{ backgroundColor: $vuetify.theme.currentTheme.brand }"
      >
        <div class="title-wrap">
          <v-icon left color="white">mdi-palette</v-icon>
          <span>Select Theme</span>
        </div>

        <v-spacer />

        <v-btn icon @click="close">
          <v-icon color="white">mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-6">
        <v-row dense>
          <v-col
            v-for="(theme, index) in derivedThemes"
            :key="index"
            cols="2"
          >
            <div
              class="theme-tile"
              :class="{ active: activeBrand === theme.brand }"
              :style="{ backgroundColor: theme.brand }"
              @click="applyTheme(theme)"
            >
              <div
                class="theme-light"
                :style="{ backgroundColor: theme.brandLight }"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-text>

    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
export default {
  name: "ThemePopup",

  data() {
    return {
      dialog: false,
      themes: [
  // Row 1 – Rich soft colors (NOT washed)
  { brand: "#9575DE" }, // original lavender (unchanged)
  { brand: "#c7a6ff" }, // deeper lavender (from #f3e8ff)
  { brand: "#7fc1e6" }, // richer sky blue (from #d4f0fc)
  { brand: "#5fb3d6" }, // cyan blue
  { brand: "#8fa1e8" }, // periwinkle
  { brand: "#9a86d6" }, // muted violet

  // Row 2 – Greens & teals (clearly visible)
  { brand: "#a8d88a" }, // green (from #e6f9d6)
  { brand: "#6fc2a4" }, // eucalyptus
  { brand: "#4db6ac" }, // teal
  { brand: "#2fa4a9" }, // blue-teal
  { brand: "#66bb6a" }, // material green
  { brand: "#43a047" }, // deeper green

  // Row 3 – Warm tones (no pale pinks)
  { brand: "#e57373" }, // rose (from #ffe6e6)
  { brand: "#ef5350" }, // red
  { brand: "#f2a365" }, // peach
  { brand: "#fb8c00" }, // amber
  { brand: "#d9826b" }, // clay
  { brand: "#c68642" }, // warm brown

  // Row 4 – Neutral / professional (varied hues)
  { brand: "#546e7a" }, // blue-grey
  { brand: "#455a64" }, // graphite
  { brand: "#6d4c41" }, // brown-grey
  { brand: "#5d4037" }, // cocoa
  { brand: "#616161" }, // neutral grey
  { brand: "#37474f" }, // charcoal

  // Row 5 – Dark / premium (CLEAR variety)
  { brand: "#1e3a5f" }, // deep steel blue
  { brand: "#263238" }, // material dark
  { brand: "#2e2a5c" }, // deep indigo-purple
  { brand: "#4a148c" }, // royal purple
  { brand: "#1b5e20" }, // forest green
  { brand: "#3e2723" }  // espresso brown
],

      derivedThemes: []
    };
  },

  computed: {
    activeBrand() {
      if (
        this.$vuetify &&
        this.$vuetify.theme &&
        this.$vuetify.theme.currentTheme
      ) {
      return this.$vuetify.theme.currentTheme.brand;
    }
      return null;
    }
  },

  mounted() {
    const savedBrand = localStorage.getItem("theme_color");
    if (savedBrand) {
      const brandLight = this.deriveBrandLight(savedBrand);
      const textColor = this.getTextColorForBackground(savedBrand);

      this.$vuetify.theme.currentTheme.brand = savedBrand;
      this.$vuetify.theme.currentTheme.brandLight = brandLight;
      this.$vuetify.theme.currentTheme.onBrand = textColor;
    }
    // Derive brandLight ONCE after component is ready
    this.derivedThemes = this.themes.map(t => {
      return {
        brand: t.brand,
        brandLight: this.deriveBrandLight(t.brand)
      };
    });
  },

  methods: {
    deriveBrandLight(brand) {
      const num = parseInt(brand.slice(1), 16);

      let r = (num >> 16) & 255;
      let g = (num >> 8) & 255;
      let b = num & 255;
      r = Math.round(r + (255 - r) * 0.7);
      g = Math.round(g + (255 - g) * 0.7);
      b = Math.round(b + (255 - b) * 0.7);

      return (
        "#" +
        ((1 << 24) + (r << 16) + (g << 8) + b)
          .toString(16)
          .slice(1)
      );
    },
    getTextColorForBackground(hexColor) {
      if (!hexColor) return "#000000";

      const hex = hexColor.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);

      // luminance formula
      const luminance =
        0.2126 * r +
        0.7152 * g +
        0.0722 * b;

      // Threshold
      return luminance >= 135 ? "#1e293b" : "#FFFFFF"; //
    },


    // ---------- PUBLIC API ---------- 
    open() {
      this.dialog = true;
    },

    close() {
      this.dialog = false;
    },

    // ---------- APPLY THEME ---------- 
    applyTheme(theme) {
  if (!theme || !theme.brand) return;

  const brand = theme.brand;
  const brandLight = this.deriveBrandLight(brand);
  const textColor = this.getTextColorForBackground(brand);

  // Apply immediately 
  this.$vuetify.theme.currentTheme.brand = brand;
  this.$vuetify.theme.currentTheme.brandLight = brandLight;
  this.$vuetify.theme.currentTheme.onBrand = textColor;

  // Persist locally 
  localStorage.setItem("theme_color", brand);
  localStorage.setItem("theme_text", textColor);

  // Persist to backend (NON-BLOCKING) 
  axios.post(
    "/App/setPreferredThemeColor",
    {
      preferred_theme_color: brand
    },
    {
      headers: {
        "EPS-token": localStorage.getItem("EPS-token"),
        "EPS-uid": localStorage.getItem("EPS-uid"),
        "router-path": "/theme"
      }
    }
  ).catch(err => {
    // Important: DO NOT redirect / logout on failure
    console.warn("Theme save failed (ignored)", err);
  });

  this.close();
}

  }
};
</script>




<style scoped>
.theme-card {
  /* border-radius: 25px; */
  overflow: hidden;
}
::v-deep .v-dialog {
  border-radius: 32px;
}

/* Header */
.theme-header {
  color: white;
  font-weight: 600;
}

.title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Theme tiles */
.theme-tile {
  height: 64px;
  border-radius: 14px;
  cursor: pointer;
  position: relative;
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.18);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.theme-tile:hover {
  transform:
    perspective(1200px)
    translateY(-10px)
    rotateZ(-2.5deg)
    scale(1.055);
  box-shadow:
    0 38px 68px rgba(0, 0, 0, 0.45);
}

/* .theme-tile:hover {
  transform: translateY(-4px) scale(1.04);
  box-shadow: 0 12px 22px rgba(0, 0, 0, 0.28);
} */

/* Small accent dot */
.theme-light {
  position: absolute;
  bottom: 6px;
  right: 6px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid white;
}


.theme-tile.active::after {
  content: "✓";
  position: absolute;
  top: 8px;
  right: 10px;
  color: white;
  font-size: 18px;
  font-weight: bold;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
}

</style>